/**
 * CORE-901 — the access decision for the hidden gamification QA-preview, kept as
 * a pure function in a dependency-free module.
 *
 * Why it lives apart from the hook: `useFeatureFlags` pulls in the api client and
 * AuthContext (and through them Sentry's ESM build), which this suite's Node
 * jest cannot load. The decision is the part worth pinning by test, so it is the
 * part kept importable on its own.
 *
 * The answer is deliberately THREE-valued. `/api/features` arrives
 * asynchronously and the flags hook serves an empty map until it does, so a
 * two-valued gate reads «no flag yet» as «access denied» — and the screen acts
 * on a denial by navigating away. That bounced the visitor off the page before
 * the flag granting them access ever arrived, and only on prod: on dev the
 * synchronous URL override answers first and hides the whole problem.
 */
export type PreviewAccess = boolean | null;

export function resolveGamificationPreviewAccess(input: {
  /** Has `/api/features` answered at least once this session? */
  flagsLoaded: boolean;
  /** Value of the `gamification_preview` flag, if the map carries one. */
  flag: boolean | undefined;
  /** `?qa=gamification` on a non-prod web build — synchronous, nothing to wait for. */
  urlOverride: boolean;
}): PreviewAccess {
  if (input.urlOverride) return true;
  if (!input.flagsLoaded) return null;
  return input.flag === true;
}
